/**
 * Real Leagues API client.
 *
 * Thin, typed wrappers over the backend `/leagues` routes (see
 * backend/controllers/leagues.controller.js). The backend already serializes
 * rows into the same shape as the frontend `League` type, so responses are
 * consumed with minimal mapping.
 *
 * Auth + base URL follow the app convention: `process.env.API_URL` for the base
 * and `getAuthHeaders()` for the Bearer token.
 */

import { getAuthHeaders } from "../../../utils/authHeaders";
import type {
    CreateLeagueInput,
    League,
    LeagueBullionBattle,
    LeagueBullionBattleViewerSlip,
    LeagueGameKey,
} from "./types";
import type { LeagueResultsShareSnapshot } from "./leagueResultsShare";
import type { LeagueListUnchangedStub } from "./leagueListFinal";
import {
    isLeagueReactionEmoji,
    type LeagueReactionEmoji,
    type LeagueReactionPayload,
} from "./leagueReactions";
import { hydrateWireLeague, type WireLeague } from "./leagueWire";

/** Error thrown by any client call. `code` mirrors the backend `error` field. */
export class LeagueApiError extends Error {
    code: string;
    status: number;
    constructor(message: string, code: string, status: number) {
        super(message);
        this.name = "LeagueApiError";
        this.code = code;
        this.status = status;
    }
}

function apiBase(): string {
    const base = process.env.API_URL;
    if (!base) throw new LeagueApiError("API is not configured.", "no_api_url", 0);
    return base.replace(/\/+$/, "");
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
    let response: Response;
    try {
        response = await fetch(`${apiBase()}${path}`, {
            ...init,
            headers: { ...getAuthHeaders(), ...(init?.headers ?? {}) },
        });
    } catch (networkError) {
        throw new LeagueApiError("Network error. Please try again.", "network_error", 0);
    }

    let payload: unknown = null;
    try {
        payload = await response.json();
    } catch {
        payload = null;
    }

    if (!response.ok) {
        const body = (payload ?? {}) as { error?: string; message?: string };
        throw new LeagueApiError(
            body.message || "Something went wrong.",
            body.error || `http_${response.status}`,
            response.status,
        );
    }
    return (payload ?? {}) as T;
}

/** GET /leagues — the viewer's leagues + pending invites. */
export async function apiListLeagues(
    knownFinalFingerprint?: string | null,
): Promise<Array<League | LeagueListUnchangedStub>> {
    const query = knownFinalFingerprint ? `?final=${encodeURIComponent(knownFinalFingerprint)}` : "";
    const data = await request<{ leagues?: Array<League | LeagueListUnchangedStub> }>(`/leagues${query}`, {
        method: "GET",
    });
    return Array.isArray(data.leagues) ? data.leagues : [];
}

/** GET /leagues/public — browse public arenas (`tab=active|past`). */
export async function apiListPublicLeagues(tab: "active" | "past" = "active"): Promise<League[]> {
    const data = await request<{ leagues?: Array<League | WireLeague> }>(
        `/leagues/public?tab=${encodeURIComponent(tab)}`,
        { method: "GET" },
    );
    if (!Array.isArray(data.leagues)) return [];
    return data.leagues
        .map((league) => hydrateWireLeague(league))
        .filter((league): league is League => league != null);
}

/**
 * POST /leagues/feed — the list poll in compact wire form (hydrate rows with `hydrateWireLeague`).
 * Falls back to `GET /leagues` against a backend that does not serve the feed yet.
 */
export async function apiLeaguesFeed(
    knownFinalFingerprint: string | null,
    have: Record<string, string[]>,
): Promise<Array<League | WireLeague | LeagueListUnchangedStub>> {
    try {
        const data = await request<{ leagues?: Array<League | WireLeague | LeagueListUnchangedStub> }>(
            "/leagues/feed",
            { method: "POST", body: JSON.stringify({ final: knownFinalFingerprint ?? undefined, have }) },
        );
        return Array.isArray(data.leagues) ? data.leagues : [];
    } catch (e) {
        if (e instanceof LeagueApiError && e.status === 404) return apiListLeagues(knownFinalFingerprint);
        throw e;
    }
}

/** GET /leagues/:id — one league (detail). */
export async function apiGetLeague(
    leagueId: string,
    opts?: { fresh?: boolean },
): Promise<League | null> {
    const query = new URLSearchParams({ wire: "1" });
    if (opts?.fresh) query.set("fresh", "1");
    const data = await request<{ league?: League | WireLeague }>(
        `/leagues/${encodeURIComponent(leagueId)}?${query}`,
        { method: "GET" },
    );
    return data.league ? hydrateWireLeague(data.league) : null;
}

/** GET /leagues/by-code/:code — join-landing lookup by share code. */
export async function apiGetLeagueByCode(code: string): Promise<League | null> {
    const data = await request<{ league?: League }>(`/leagues/by-code/${encodeURIComponent(code)}`, {
        method: "GET",
    });
    return data.league ?? null;
}

/** GET /leagues/vip-status — whether the viewer can create leagues. */
export async function apiGetVipStatus(): Promise<boolean> {
    const data = await request<{ is_vip?: boolean }>("/leagues/vip-status", { method: "GET" });
    return Boolean(data.is_vip);
}

/** POST /leagues — create a league (registered accounts; not guests). Returns the created league. */
export async function apiCreateLeague(input: CreateLeagueInput): Promise<League> {
    const data = await request<{ league?: League }>("/leagues", {
        method: "POST",
        body: JSON.stringify({
            name: input.name,
            scope: input.scope,
            entryFeeRubies: input.entryFeeRubies,
            endsAt: input.endsAt,
            fixtureId: input.fixtureId,
            fixture: input.fixture,
            includedGames: input.includedGames,
        }),
    });
    if (!data.league) throw new LeagueApiError("League was not created.", "server_error", 500);
    return data.league;
}

/** POST /leagues/join — join by id or share code. Returns the joined league. */
export async function apiJoinLeague(target: {
    leagueId?: string;
    code?: string;
    /** Public player id of who shared the link (`invitedBy`) — private leagues auto-friend. */
    inviterPlayerId?: string;
}): Promise<League> {
    const inviter =
        target.inviterPlayerId != null && String(target.inviterPlayerId).trim() !== ""
            ? String(target.inviterPlayerId).trim()
            : undefined;
    const data = await request<{ league?: League }>("/leagues/join", {
        method: "POST",
        body: JSON.stringify({
            leagueId: target.leagueId,
            code: target.code,
            ...(inviter ? { inviter_player_id: inviter, invitedBy: inviter } : {}),
        }),
    });
    if (!data.league) throw new LeagueApiError("Could not join the league.", "server_error", 500);
    return data.league;
}

/** POST /leagues/:id/top-up — pay rubies for more league chips (+ grow prize pot). */
export async function apiTopUpLeagueSeat(leagueId: string): Promise<League> {
    const data = await request<{ league?: League }>(`/leagues/${encodeURIComponent(leagueId)}/top-up`, {
        method: "POST",
        body: JSON.stringify({}),
    });
    if (!data.league) throw new LeagueApiError("Could not top up league balls.", "server_error", 500);
    return data.league;
}

/** POST /leagues/:id/decline — decline a pending invite. */
export async function apiDeclineLeague(leagueId: string): Promise<void> {
    await request(`/leagues/${encodeURIComponent(leagueId)}/decline`, { method: "POST" });
}

/** POST /leagues/:id/invite — invite friends by user id. Returns invited count + fresh league. */
export async function apiInviteFriends(
    leagueId: string,
    userIds: number[],
): Promise<{ invited: number; league: League | null }> {
    const data = await request<{ invited?: number; league?: League }>(
        `/leagues/${encodeURIComponent(leagueId)}/invite`,
        { method: "POST", body: JSON.stringify({ userIds }) },
    );
    return { invited: Number(data.invited) || 0, league: data.league ?? null };
}

/**
 * Market descriptor captured with a league play so the backend can settle it directly from the real
 * cricket market result (mirrors the fields real Trader settlement reads off `trades.marketdetails`).
 */
export interface PlaceLeaguePlayMarketDetails {
    /** 'Win' (winning selection name echoed as Result) or 'Line' (Over/Under vs marketline). */
    marketclass?: string;
    /** Graded selection: team/player name for Win markets, or 'Over' / 'Under' for line markets. */
    selectionname?: string;
    /** Numeric line for Over/Under markets. */
    marketline?: string | number;
    marketTypeId?: string | number;
    marketname?: string;
    marketteam?: string;
    marketplayer?: string;
    /** Squares: which over of which innings, and the full 6-ball grid staked on. */
    inningsNumber?: number;
    overNumber?: number;
    totalChips?: number;
    selections?: Record<string, { run?: string; chips?: number; odds?: string | number }>;
}

/** The market reference captured when a league play is placed against a real cricket outcome. */
export interface PlaceLeaguePlayInput {
    game: LeagueGameKey;
    stake: number;
    multiplier: number;
    selection?: string;
    matchId?: string;
    marketId?: string;
    marketLineId?: string;
    selectionKey?: string;
    /** When true, server skips the in-play 2s delay (same as `/trade/submit`). */
    prematch?: boolean;
    /** ISO timestamp when the underlying market is expected to resolve. */
    settlesAt?: string;
    /** Captured so the play can settle from the real market result without a peer trade. */
    marketDetails?: PlaceLeaguePlayMarketDetails;
}

/** POST /leagues/:id/plays — place an isolated league play. Returns id + fresh league. */
export async function apiPlaceLeaguePlay(
    leagueId: string,
    input: PlaceLeaguePlayInput,
): Promise<{ playId: string; league: League | null }> {
    const data = await request<{ playId?: string; league?: League }>(
        `/leagues/${encodeURIComponent(leagueId)}/plays`,
        {
            method: "POST",
            body: JSON.stringify({
                game: input.game,
                stake: input.stake,
                multiplier: input.multiplier,
                selection: input.selection,
                matchId: input.matchId,
                marketId: input.marketId,
                marketLineId: input.marketLineId,
                selectionKey: input.selectionKey,
                settlesAt: input.settlesAt,
                marketDetails: input.marketDetails,
                prematch: input.prematch === true,
            }),
        },
    );
    return { playId: String(data.playId ?? ""), league: data.league ?? null };
}

/**
 * GET /leagues/:id/bullion-battle — the league's shared Bullion battle (created lazily server-side
 * once the board is ready). `reason` explains a null battle (e.g. board not ready).
 */
export async function apiGetBullionBattle(
    leagueId: string,
): Promise<{ battle: LeagueBullionBattle | null; reason: string | null }> {
    const data = await request<{ battle?: LeagueBullionBattle | null; reason?: string | null }>(
        `/leagues/${encodeURIComponent(leagueId)}/bullion-battle`,
        { method: "GET" },
    );
    return { battle: data.battle ?? null, reason: data.reason ?? null };
}

/**
 * POST /leagues/:id/reactions — throw an ephemeral emoji at a joined competitor (SSE fan-out).
 */
export async function apiPostLeagueReaction(
    leagueId: string,
    input: { targetUserId: number; emoji: LeagueReactionEmoji; reactionId?: string },
): Promise<LeagueReactionPayload> {
    const data = await request<{
        ok?: boolean;
        reactionId?: string;
        leagueId?: string;
        fromUserId?: number;
        fromPlayerName?: string;
        targetUserId?: number;
        emoji?: LeagueReactionEmoji;
    }>(`/leagues/${encodeURIComponent(leagueId)}/reactions`, {
        method: "POST",
        body: JSON.stringify({
            targetUserId: input.targetUserId,
            emoji: input.emoji,
            reactionId: input.reactionId,
        }),
    });
    return {
        reactionId: String(data.reactionId ?? input.reactionId ?? ""),
        leagueId: String(data.leagueId ?? leagueId),
        fromUserId: Number(data.fromUserId) || 0,
        fromPlayerName: String(data.fromPlayerName ?? "").trim(),
        targetUserId: Number(data.targetUserId) || input.targetUserId,
        emoji: isLeagueReactionEmoji(data.emoji) ? data.emoji : input.emoji,
    };
}

function mapReactionPayload(raw: {
    reactionId?: string;
    leagueId?: string;
    fromUserId?: number;
    fromPlayerName?: string;
    targetUserId?: number;
    emoji?: string;
    extraCount?: number;
}): LeagueReactionPayload | null {
    if (!isLeagueReactionEmoji(raw.emoji)) return null;
    const reactionId = String(raw.reactionId ?? "").trim();
    const leagueId = String(raw.leagueId ?? "").trim();
    const fromUserId = Number(raw.fromUserId) || 0;
    const targetUserId = Number(raw.targetUserId) || 0;
    if (!reactionId || !leagueId || fromUserId <= 0 || targetUserId <= 0) return null;
    const extraRaw = Number(raw.extraCount) || 0;
    return {
        reactionId,
        leagueId,
        fromUserId,
        fromPlayerName: String(raw.fromPlayerName ?? "").trim() || `Player ${fromUserId}`,
        targetUserId,
        emoji: raw.emoji,
        ...(extraRaw > 0 ? { extraCount: extraRaw } : {}),
    };
}

export type PendingLeagueReactions = {
    reactions: LeagueReactionPayload[];
    missed: number;
};

/** GET /leagues/reactions/pending — unseen throws aimed at the viewer (newest first). */
export async function apiListPendingLeagueReactions(
    leagueId?: string,
): Promise<PendingLeagueReactions> {
    const query = leagueId ? `?leagueId=${encodeURIComponent(leagueId)}` : "";
    const data = await request<{
        reactions?: Array<Parameters<typeof mapReactionPayload>[0]>;
        missed?: number;
    }>(`/leagues/reactions/pending${query}`, { method: "GET" });
    const reactions = (data.reactions ?? [])
        .map(mapReactionPayload)
        .filter((row): row is LeagueReactionPayload => row != null);
    const missedRaw = Number(data.missed);
    return {
        reactions,
        missed: Number.isFinite(missedRaw) ? Math.max(missedRaw, reactions.length) : reactions.length,
    };
}

/** POST /leagues/reactions/ack — stop replaying these throws on the next visit. */
export async function apiAckLeagueReactions(
    reactionIds: string[],
    opts?: { all?: boolean; leagueId?: string },
): Promise<void> {
    if (opts?.all) {
        await request(`/leagues/reactions/ack`, {
            method: "POST",
            body: JSON.stringify({
                all: true,
                ...(opts.leagueId ? { leagueId: opts.leagueId } : {}),
            }),
        });
        return;
    }
    const ids = [...new Set(reactionIds.map((id) => String(id || "").trim()).filter(Boolean))];
    if (ids.length === 0) return;
    await request(`/leagues/reactions/ack`, {
        method: "POST",
        body: JSON.stringify({ reactionIds: ids }),
    });
}

export type LeagueReactionHistoryItem = {
    reactionId: string;
    fromUserId: number;
    fromPlayerName: string;
    emoji: LeagueReactionEmoji;
    createdAt: string | null;
};

/** GET /leagues/:id/reactions?targetUserId= — recent throws at a member. */
export async function apiListLeagueReactionsAtTarget(
    leagueId: string,
    targetUserId: number,
): Promise<LeagueReactionHistoryItem[]> {
    const data = await request<{
        reactions?: Array<{
            reactionId?: string;
            fromUserId?: number;
            fromPlayerName?: string;
            emoji?: string;
            createdAt?: string | null;
        }>;
    }>(
        `/leagues/${encodeURIComponent(leagueId)}/reactions?targetUserId=${encodeURIComponent(String(targetUserId))}`,
        { method: "GET" },
    );
    return (data.reactions ?? [])
        .map((row) => {
            if (!isLeagueReactionEmoji(row.emoji)) return null;
            const reactionId = String(row.reactionId ?? "").trim();
            const fromUserId = Number(row.fromUserId) || 0;
            if (!reactionId || fromUserId <= 0) return null;
            return {
                reactionId,
                fromUserId,
                fromPlayerName: String(row.fromPlayerName ?? "").trim() || `Player ${fromUserId}`,
                emoji: row.emoji,
                createdAt: typeof row.createdAt === "string" ? row.createdAt : null,
            };
        })
        .filter((row): row is LeagueReactionHistoryItem => row != null);
}

/**
 * POST /leagues/:id/bullion-battle/plays — enter (or replace) the viewer's Bullion battle slip.
 * League coins (the battle entry stake) are held. Returns the play id + fresh league + battle.
 */
export async function apiEnterBullionBattle(
    leagueId: string,
    slip: LeagueBullionBattleViewerSlip,
): Promise<{ playId: string; league: League | null; battle: LeagueBullionBattle | null }> {
    const data = await request<{ playId?: string; league?: League; battle?: LeagueBullionBattle | null }>(
        `/leagues/${encodeURIComponent(leagueId)}/bullion-battle/plays`,
        { method: "POST", body: JSON.stringify({ slip }) },
    );
    return {
        playId: String(data.playId ?? ""),
        league: data.league ?? null,
        battle: data.battle ?? null,
    };
}

/** POST /leagues/results-share — mint a short `/leagues?r=CODE` link. */
export async function apiCreateLeagueResultsShare(
    snapshot: LeagueResultsShareSnapshot,
    leagueId?: string | null,
    /** Optional JPEG data URL of the 1200×630 preview card (served as og:image). */
    ogImageDataUrl?: string | null,
): Promise<string> {
    const data = await request<{ code?: string }>("/leagues/results-share", {
        method: "POST",
        body: JSON.stringify({
            snapshot,
            ...(leagueId ? { leagueId } : {}),
            ...(ogImageDataUrl ? { ogImage: ogImageDataUrl } : {}),
        }),
    });
    const code = String(data.code ?? "").trim();
    if (!code) throw new LeagueApiError("Could not create share link.", "no_code", 500);
    return code;
}

/** GET /leagues/results-share/:code — public podium snapshot for a short link. */
export async function apiGetLeagueResultsShare(code: string): Promise<LeagueResultsShareSnapshot | null> {
    const data = await request<{ snapshot?: LeagueResultsShareSnapshot }>(
        `/leagues/results-share/${encodeURIComponent(code)}`,
        { method: "GET" },
    );
    return data.snapshot ?? null;
}

/** GET /leagues/:id/public-results — podium for a finished public league (non-member viewers). */
export async function apiGetPublicLeagueResults(
    leagueId: string,
): Promise<LeagueResultsShareSnapshot | null> {
    try {
        const data = await request<{ snapshot?: LeagueResultsShareSnapshot }>(
            `/leagues/${encodeURIComponent(leagueId)}/public-results`,
            { method: "GET" },
        );
        return data.snapshot ?? null;
    } catch (error) {
        if (error instanceof LeagueApiError && (error.status === 404 || error.code === "not_found")) {
            return null;
        }
        throw error;
    }
}
